import React, {useEffect} from 'react';
import {Box, Grid} from '@mui/material';

import AutoField, {ActionType} from '../../components/AutoForm/AutoFieldNew';
import {
  longTermVul,
  longTermVulTypes,
  shortTermVul,
  shortTermVulTypes,
} from './vulnerabilities';
import {camelCaseToCapitalisedString} from '../../utils/camelCaseToCapitalisedString';
import {UPDATES} from '../../shared/UPDATES';
import {useAutoForm, useAutoField} from '../../components/AutoForm/useAutoForm';
import {Vulnerabilities} from '../../schema-types/Vulnerabilities';
import {
  AccountVulnerabilitiesFragment_primaryContact,
  AccountVulnerabilitiesFragment_secondaryContact,
} from '../../schema-types/AccountVulnerabilitiesFragment';
import {SaltusAny} from '../../utils/SaltusAny';

export const HeaderVulnerabilityFields: React.FC<{
  contact:
    | AccountVulnerabilitiesFragment_primaryContact
    | AccountVulnerabilitiesFragment_secondaryContact;
}> = ({contact}) => {
  const contactId = contact.id as string;
  const [longVuln] = useAutoField(`isVulnerable.${contactId}`);
  const [eventVuln] = useAutoField(`isEventVulnerable.${contactId}`);

  const {setFieldValue} = useAutoForm();
  useEffect(() => {
    if (!longVuln.value) {
      setFieldValue(UPDATES._1900, `longTermVulnerabilityTypes.${contactId}`, []);
    }
  }, [longVuln.value]);
  useEffect(() => {
    if (!eventVuln.value) {
      setFieldValue(UPDATES._1901, `eventVulnerabilityTypes.${contactId}`, []);
      setFieldValue(UPDATES._1902, `specialCareReasons.${contactId}`, '');
    }
  }, [eventVuln.value]);
  return (
    <>
      <Grid item xs={6} pl={2} pr={2}>
        <AutoField
          field={{
            id: `isVulnerable.${contactId}`,
            fullWidth: true,
            label: `${contact.simplifiedName} is long-term vulnerable`,
            type: ActionType.checkboxToggle,
          }}
        />
        <Box mt={2} />
        <AutoField
          field={{
            id: `longTermVulnerabilityTypes.${contactId}` as any,
            type: ActionType.checkboxGroup,
            label: `${contact.simplifiedName}'s long-term vulnerabilities`,
            options: longTermVul.map((x) => ({
              value: x,
              label: camelCaseToCapitalisedString(x).replace('vulnerability', ''),
            })),
            disabled: !longVuln.value,
            horizontal: true,
            horizontalOptionWidthXs: 12,
            horizontalOptionWidthMd: 6,
            horizontalOptionWidthLg: 6,
            size: 'small',
            boldLabel: true,
            fullWidth: true,
          }}
        />
      </Grid>
      <Grid item xs={6} pl={2} pr={2}>
        <AutoField
          field={{
            id: `isEventVulnerable.${contactId}`,
            fullWidth: true,
            label: `${contact.simplifiedName} is circumstantially vulnerable`,
            type: ActionType.checkboxToggle,
          }}
        />
        <Box mt={2} />
        <AutoField
          field={{
            id: `eventVulnerabilityTypes.${contactId}` as any,
            type: ActionType.checkboxGroup,
            label: `${contact.simplifiedName}'s circumstantial vulnerabilities`,
            boldLabel: true,
            fullWidth: true,
            disabled: !eventVuln.value,
            options: shortTermVul.map((x) => ({
              value: x,
              label: camelCaseToCapitalisedString(x).replace('vulnerability', ''),
            })),
            horizontal: true,
            horizontalOptionWidthXs: 12,
            horizontalOptionWidthMd: 6,
            horizontalOptionWidthLg: 6,

            size: 'small',
          }}
        />
        <Box mt={2} />
        <AutoField
          field={{
            id: `specialCareReasons.${contactId}`,
            fullWidth: true,
            label: 'Recent circumstance changes',
            type: ActionType.text,
            multiline: true,
            disabled: !eventVuln.value,
            minRows: 4,
            initialValue: contact?.specialCareReasons || '',
            showCounter: true,
            charLimit: 255,
          }}
        />
      </Grid>
    </>
  );
};
export function getVulnerabilitiesFields(contact: Vulnerabilities & {id: SaltusAny}) {
  const eventVulnerabilityTypes = [];
  const longTermVulnerabilityTypes = [];

  for (const keyUnprocessed of Object.keys(contact)) {
    const key = keyUnprocessed as keyof Vulnerabilities;
    if (key === '__typename') {
      // do nothing
    } else if (longTermVul.includes(key as longTermVulTypes) && contact[key] === true) {
      // if(contact.otherVulnerability) {
      longTermVulnerabilityTypes.push(key);
      // }
    } else if (shortTermVul.includes(key as shortTermVulTypes) && contact[key] === true) {
      // if(contact.otherEventVulnerability) {
      eventVulnerabilityTypes.push(key);
      // }
    }
  }
  return {
    eventVulnerabilityTypes,
    longTermVulnerabilityTypes,
  };
}
